import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { FaChevronDown, FaChevronUp, FaPlane, FaMapMarkerAlt, FaInfoCircle } from 'react-icons/fa';

// Helper function to format name
const formatName = (path) => {
  let name = path.split('/').pop().replace(/-/g, ' ');
  return name.replace(/\b\w/g, (l) => l.toUpperCase());
};

// Helper function to group URLs
const groupUrls = (urls) => {
  const groups = {
    'General Pages': [],
    'Heathrow Airport Parking': [],
    'Gatwick Airport Parking': [],
    'Manchester Airport Parking': [],
    'Stansted Airport Parking': [],
    'Birmingham Airport Parking': [],
    'Bristol Airport Parking': [],
    'Luton Airport Parking': [],
    'Other Locations': [],
  };

  urls.forEach((url) => {
    const path = url.replace('https://airportparkingdeals.co.uk', '');
    if (path === '/' || path === '') {
      groups['General Pages'].push({ path, name: 'Home' });
    } else if (path.includes('heathrow')) {
      groups['Heathrow Airport Parking'].push({ path, name: formatName(path) });
    } else if (path.includes('gatwick')) {
      groups['Gatwick Airport Parking'].push({ path, name: formatName(path) });
    } else if (path.includes('manchester')) {
      groups['Manchester Airport Parking'].push({ path, name: formatName(path) });
    } else if (path.includes('stansted')) {
      groups['Stansted Airport Parking'].push({ path, name: formatName(path) });
    } else if (path.includes('birmingham')) {
      groups['Birmingham Airport Parking'].push({ path, name: formatName(path) });
    } else if (path.includes('bristol')) {
      groups['Bristol Airport Parking'].push({ path, name: formatName(path) });
    } else if (path.includes('luton')) {
      groups['Luton Airport Parking'].push({ path, name: formatName(path) });
    } else {
      const generalPages = ['/about-us', '/contact-us', '/faqs', '/privacy-policy', '/terms-and-conditions', '/meet-and-greet-parking', '/park-and-ride-parking'];
      if (generalPages.includes(path)) {
           groups['General Pages'].push({ path, name: formatName(path) });
      } else {
           groups['Other Locations'].push({ path, name: formatName(path) });
      }
    }
  });

  return groups;
};

const Sitemap = () => {
  const [urls, setUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    const fetchSitemap = async () => {
      try {
        const response = await axios.get('/sitemap.xml', {
          headers: {
            'Content-Type': 'application/xml; charset=utf-8',
          },
        });
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(response.data, 'text/xml');
        const urlElements = xmlDoc.getElementsByTagName('url');
        const extractedUrls = Array.from(urlElements).map((urlElement) => {
          const loc = urlElement.getElementsByTagName('loc')[0].textContent;
          return loc;
        });
        setUrls(extractedUrls);
        
        // Group URLs to determine sections and set them all to expanded
        const groups = groupUrls(extractedUrls);
        const allExpanded = Object.keys(groups).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {});
        setExpandedSections(allExpanded);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching sitemap:', err);
        setError('Failed to load sitemap.');
        setLoading(false);
      }
    };

    fetchSitemap();
  }, []);

  const toggleSection = (category) => {
    setExpandedSections((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const groupedUrls = groupUrls(urls);

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex justify-center items-center text-red-600 bg-gray-50">
        <div className="text-xl font-semibold">{error}</div>
      </div>
    );
  }

  return (
    <div className="mt-16 min-h-screen py-12 px-4 sm:px-6 lg:px-8 font-sans">
       <Helmet>
        <title>Sitemap | Airport Parking Deals</title>
        <meta
          name="description"
          content="Explore our comprehensive sitemap to find parking deals at major UK airports including Heathrow, Gatwick, Manchester, and more."
        />
      </Helmet>
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4 tracking-tight">
            Site<span className="text-blue-600">map</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Easily navigate through our website to find the best airport parking deals and information.
          </p>
        </div>
        
        <div className="space-y-6">
          {Object.entries(groupedUrls).map(([category, links]) => (
            links.length > 0 && (
              <div key={category} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden border border-gray-100">
                <button
                  onClick={() => toggleSection(category)}
                  className="w-full flex items-center justify-between p-6 bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none"
                  aria-expanded={expandedSections[category]}
                >
                  <div className="flex items-center space-x-4">
                    <div className={`p-3 rounded-full ${category === 'General Pages' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'}`}>
                      {category === 'General Pages' ? <FaInfoCircle size={20} /> : <FaPlane size={20} className="transform -rotate-45" />}
                    </div>
                    <h2 className="text-xl font-bold text-gray-800 text-left">
                      {category}
                    </h2>
                    <span className="text-sm text-gray-500 font-medium bg-gray-100 px-2 py-1 rounded-full">
                      {links.length} Links
                    </span>
                  </div>
                  <div className="text-gray-400">
                    {expandedSections[category] ? <FaChevronUp size={20} /> : <FaChevronDown size={20} />}
                  </div>
                </button>
                
                <div 
                  className={`transition-all duration-300 ease-in-out ${expandedSections[category] ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <div className="p-6 pt-0 border-t border-gray-100 bg-gray-50">
                    <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                      {links.map((link, index) => (
                        <li key={index}>
                          <Link
                            to={link.path}
                            className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 hover:bg-white p-2 rounded-lg transition-all duration-200 group"
                            title={link.name}
                          >
                            <FaMapMarkerAlt size={14} className="text-gray-400 group-hover:text-blue-500 transition-colors" />
                            <span className="truncate font-medium">{link.name}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )
          ))}
        </div>
        
        <div className="mt-12 text-center border-t border-gray-200 pt-8">
          <p className="text-gray-500 font-medium">
            Total Links: <span className="text-blue-600 font-bold">{urls.length}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Sitemap;
